import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CALENDAR_URL, MEALS_URL } from "../../MainAppPanel";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../AppContextProvider";
import { checkout, getSavedCards } from "../../api/CafeteriaClient";
import { CreditCard as SavedCreditCard } from "../../models/CreditCard";
import { GiftCard as SavedGiftCard } from "../../models/GiftCard";
import ShoppingCartTable from "./ShoppingCartTable";
import CheckoutForm from "./CheckoutForm";
import { AxiosError } from "axios";
import ConfirmDialog from "../ConfirmDialog";
import { Role } from "../../models/User";

const ShoppingCartPage: React.FC = () => {
  const {
    shoppingCart,
    user,
    setUser,
    orders,
    setOrders,
    school,
    setShoppingCart,
    scheduledMenus,
    setSnackbarErrorMsg,
    students,
  } = useContext(AppContext);

  // "creditcard" | "giftcard" | "donate" | Square saved-card id
  const [paymentMethod, setPaymentMethod] = useState("creditcard");
  const [useCredits, setUseCredits] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  // const [useCredits, setUseCredits] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [savedCreditCards, setSavedCreditCards] = useState<SavedCreditCard[]>(
    []
  );
  const [savedGiftCards, setSavedGiftCards] = useState<SavedGiftCard[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    const getCards = async () => {
      try {
        const cards = await getSavedCards();
        if (cards.creditCards.length) {
          setSavedCreditCards(cards.creditCards);
        }
        if (cards.giftCards.length) {
          setSavedGiftCards(cards.giftCards);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        /* empty */
      }
    };

    if (user.role === Role.ADMIN) {
      return;
    }

    if (
      shoppingCart.items
        .filter((item) => item.studentId)
        .some(
          (item) =>
            !students
              .find((s) => s.id === item.studentId)
              ?.parents.includes(user.id)
        )
    ) {
      return;
    }

    getCards();
  }, []);

  useEffect(() => {
    if (!showThankYou && !shoppingCart.items.length) {
      navigate(CALENDAR_URL);
    }
  }, [shoppingCart]);

  const handleCheckout = async (paymentToken: string) => {
    try {
      const completedOrder = await checkout(
        useCredits,
        paymentToken,
        shoppingCart,
        saveCard,
        sendEmail
      );

      if (useCredits) {
        setUser({...user, availableCredits: Math.max(0, user.availableCredits - completedOrder.appliedCredits)});
      }

      setOrders(orders.concat(completedOrder));
      setShowThankYou(true);
    } catch (error) {
      const axiosError = error as AxiosError;
      setSnackbarErrorMsg(
        "Error occurred while checking out: " +
          (axiosError.response?.data?.toString() ??
            axiosError.response?.statusText ??
            "Unknown server error")
      );
    }
  };

  const handleThankYouClose = () => {
    setShoppingCart({ items: [] });
    setShowThankYou(false);
    navigate(user.role === Role.ADMIN ? CALENDAR_URL : MEALS_URL);
  };

  const handlePaymentChanged = (nextPaymentMethod: string) => {
    if (nextPaymentMethod !== "creditcard") {
      setSaveCard(false);
    }
    if (nextPaymentMethod === "donate") {
      setSendEmail(false);
      setUseCredits(false);
    }
    setPaymentMethod(nextPaymentMethod);
  };

  const handleUseCreditsChange = (checked: boolean) => {
    setUseCredits(checked);
  };

  let total = shoppingCart.items
    .map((item) => {
      const menu = scheduledMenus.find((menu) => menu.id == item.dailyMenuId)!;
      return item.isDrinkOnly ? menu.drinkOnlyPrice : menu.price;
    })
    .reduce((p1, p2) => p1 + p2, 0);
  if (useCredits) {
    total = Math.max(0, total - user.availableCredits);
  }

  return (
    <Box
      sx={{
        maxHeight: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <ShoppingCartTable
        hidePrice={paymentMethod === "donate"}
        editable={true}
      ></ShoppingCartTable>

      <Stack direction="column" gap={4} alignItems="center">
        <Stack>
          {user.availableCredits > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCredits}
                  disabled={paymentMethod === "donate"}
                  onChange={(e) => handleUseCreditsChange(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Use my credits (Avail.: ${user.availableCredits.toFixed(2)})
                </Typography>
              }
            />
          )}
        </Stack>

        {total === 0 ? (
          <Button
            variant="contained"
            onClick={() => handleCheckout(paymentMethod)}
          >
            Submit Order
          </Button>
        ) : (
          <CheckoutForm
            school={school}
            user={user}
            selectedCard={paymentMethod}
            total={total}
            saveCard={saveCard}
            sendEmail={sendEmail}
            savedCreditCards={savedCreditCards}
            savedGiftCards={savedGiftCards}
            showDonateOption={user.role === Role.ADMIN}
            onCardSelected={handlePaymentChanged}
            onSaveCardChange={setSaveCard}
            onSendEmailChange={
              user.role === Role.ADMIN ? undefined : setSendEmail
            }
            onPayWithSavedCard={handleCheckout}
            onTokenReceived={(tokenResult, buyer) => {
              const result = tokenResult as { status: string; token?: string };
              if (result.status !== "OK") {
                console.log("Tokenization failed");
              } else {
                console.log(buyer);
                handleCheckout(result.token!);
              }
            }}
          />
        )}
      </Stack>

      {showThankYou && (
        <ConfirmDialog
          open={true}
          onOk={handleThankYouClose}
          hideCancelButton={true}
          onCancel={handleThankYouClose}
          title={
            user.role === Role.ADMIN
              ? "Order Submitted"
              : "Thank You For Your Order"
          }
        >{user.role === Role.ADMIN ? (
          <Typography>
            Your order has been successfully placed. Click OK to return to the calendar.
          </Typography>
        ) : (
          <Typography>
            Your order has been successfully placed. Click OK to view your
            upcoming ordered meals.
          </Typography>
        )}
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default ShoppingCartPage;
